import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {useAccessToken, useIsUnitUser, useUserType} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import {useQueryState} from "use-location-state"
import {usePaging} from "../../hooks/usePaging"
import {findIncomingWires, IncomingWire, IncomingWireStatus} from "../../resources/incomingWire"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {Filter} from "../Filter/Filter"
import {startCase} from "lodash"
import {ErrorDocument} from "../../resources/common"
import moment from "moment"
import {useModal} from "react-modal-hook"
import {RejectIncomingWireModal} from "./RejectIncomingWireModal"
import {ApproveIncomingWireModal} from "./ApproveIncomingWire"
import Icon from "../Icon/Icon"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import {findOrgs, Org} from "../../resources/org"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {Cents} from "../../resources/transaction"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Bank, findBanks} from "../../resources/bank"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {AllowedIncomingWiresColumns} from "./IncomingWires.types"
import {IncomingWiresTable} from "./IncomingWiresTable"
import SearchBox from "../SearchBox/SearchBox"

interface IncomingWiresProps {
	limit?: number
	includedColumns: AllowedIncomingWiresColumns[]
	enableStatusFilter?: boolean
	enableOrgFilter?: boolean
	enableBankFilter?: boolean
	enableAmountFilter?: boolean
	title?: string
	fullHeight?: boolean
	stickyAction?: boolean
	enablePaging?: boolean
}

interface IncomingWireButtonsProps {
	incomingWire: IncomingWire
	refresh: () => void
}

export function IncomingWireButtons({incomingWire, refresh}: IncomingWireButtonsProps) {
	const [showReject, hideReject] = useModal(() => (
		<RejectIncomingWireModal close={hideReject} incomingWire={incomingWire} onSuccess={refresh} />
	))
	const [showApprove, hideApprove] = useModal(() => (
		<ApproveIncomingWireModal close={hideApprove} incomingWire={incomingWire} onSuccess={refresh} />
	))

	return (
		<>
			{incomingWire.attributes.status === "Errored" && (
				<>
					<HasPermission resource="incomingWire.return" action="update">
						<button className="button is-danger" onClick={showReject}>
							<span className="icon">
								<Icon icon="interface-delete-interface-essential" size={12} />
							</span>
							<span> Reject </span>
						</button>
					</HasPermission>

					<HasPermission resource="incomingWire.process" action="update">
						<button className="button is-success" onClick={showApprove}>
							<span className="icon">
								<Icon icon="interface-validation-check--interface-essential" size={12} />
							</span>
							<span> Approve </span>
						</button>
					</HasPermission>
				</>
			)}
			{["Processed"].includes(incomingWire.attributes.status) && (
				<>
					<HasPermission resource="incomingWire.return" action="update">
						<button className="button is-danger" onClick={showReject}>
							<span className="icon">
								<Icon icon="interface-delete-interface-essential" size={12} />
							</span>
							<span> Return </span>
						</button>
					</HasPermission>
				</>
			)}
		</>
	)
}

export default function IncomingWires({
	limit = 25,
	includedColumns,
	stickyAction = false,
	fullHeight = false,
	enablePaging = true,
	enableStatusFilter = true,
	enableOrgFilter = true,
	enableAmountFilter = true,
	enableBankFilter = true,
	title,
}: IncomingWiresProps) {
	const prefix = "inc-wire"
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()

	const [statuses, setStatuses] = useQueryState(
		`${prefix}-filter[status]`,
		enableStatusFilter ? [IncomingWireStatus.Errored] : []
	)
	const statusesOptions = new Map(Object.values(IncomingWireStatus).map((v) => [v, startCase(v)]))
	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")

	const onSearch = (searchTerm: string) => {
		setQuery(searchTerm)
		reset(limit)
	}
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search by Account/Customer/Amount", 500, onSearch)

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findIncomingWires(
				query,
				accessToken,
				offset,
				limit,
				statuses,
				filteredOrgs,
				filteredBanks,
				fromAmount,
				toAmount,
				since,
				until
			),
		(x) => x.data.length,
		[
			refreshToken,
			[...statuses, ...filteredOrgs, ...filteredBanks].join(","),
			fromAmount,
			toAmount,
			since,
			until,
			query,
		],
		`${prefix}-`,
		true
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const banks = useIsUnitUser()
		? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
		: AsyncResult.pending<Bank[], ErrorDocument>()
	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable={true}
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	return (
		<DataTableCard className={"incoming-wires-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={dateTime}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableBankFilter ? [bankFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
				]}
				searchBox={searchBox}
				title={title}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: incomingWires}) => {
					return (
						<IncomingWiresTable
							incomingWires={incomingWires.data}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							include={incomingWires.included}
							includedColumns={includedColumns}
							isUsingPaging={enablePaging}
							stickyAction={stickyAction}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
